import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { useLeadActions } from './leads/hooks/useLeadActions';
import LeadsHeader from './leads/components/LeadsHeader';
import LeadsContent from './leads/components/LeadsContent';
import LeadsSearchBar from './leads/components/LeadsSearchBar';
import LeadsPagination from './leads/components/LeadsPagination';
import { getCompanyMembers } from '@/services/companyMemberService';
import { getLeadsPage } from '@/services/leads/queries/getLeadsPage';
import { Lead } from '@/types/lead';
import { useSubscription } from '@/hooks/useSubscription';
import { CheckCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAILeadFilter } from '@/hooks/useAILeadFilter';
import { SortBy, LocationFilter, ContactStatusFilter } from '@/types/filters';
import { toTitleCase } from '@/utils/toTitleCase';
import { LeadFilters } from '@/types/filters';
import { useDebounce } from '@/hooks/useDebounce';
import { EnrichmentProgressBar } from '@/components/ui/EnrichmentProgressBar';
import { Grid, Rows, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

const FILTERS_STORAGE_KEY = 'leadFilters';

const getStoredFilters = (companyId: string): LeadFilters | null => {
  try {
    const stored = localStorage.getItem(`${FILTERS_STORAGE_KEY}_${companyId}`);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const saveFilters = (companyId: string, filters: LeadFilters) => {
  try {
    localStorage.setItem(`${FILTERS_STORAGE_KEY}_${companyId}`, JSON.stringify(filters));
  } catch { /* ignore */ }
};

const Index = () => {
  const { toast } = useToast();
  const { user, isLoading: authLoading, activeCompany } = useAuth();
  const { subscription } = useSubscription(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [isSelectAllFiltered, setIsSelectAllFiltered] = useState(false);

  // Hydrate filters from localStorage
  const storedFilters = activeCompany?.id ? getStoredFilters(activeCompany.id) : null;
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(storedFilters?.showFavoritesOnly ?? false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(storedFilters?.selectedCategories ?? []);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(storedFilters?.selectedUserId ?? 'my-leads');
  const [currentView, setCurrentView] = useState<'card' | 'list' | 'table'>(storedFilters?.currentView ?? 'card');
  const [sortBy, setSortBy] = useState<SortBy>(storedFilters?.sortBy ?? 'newest');
  const [locationFilter, setLocationFilter] = useState<LocationFilter | null>(storedFilters?.locationFilter ?? null);
  const [selectedSector, setSelectedSector] = useState<string | null>(storedFilters?.selectedSector ?? null);
  const [hasEmail, setHasEmail] = useState(storedFilters?.hasEmail ?? false);
  const [contactStatus, setContactStatus] = useState<ContactStatusFilter>(storedFilters?.contactStatus ?? 'all');
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(storedFilters?.showDuplicatesOnly ?? false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const debouncedLocationCustom = useDebounce(
    locationFilter?.type === 'custom' ? locationFilter.customText : null,
    400
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [categories, setCategories] = useState<string[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);

  const hasHydratedRef = useRef(false);

  // Effective location filter for queries (debounced for custom text)
  const effectiveLocationFilter: LocationFilter | null = locationFilter
    ? locationFilter.type === 'custom'
      ? debouncedLocationCustom?.trim()
        ? { type: 'custom', customText: debouncedLocationCustom }
        : null
      : locationFilter
    : null;

  // Save filters to localStorage when they change
  useEffect(() => {
    if (activeCompany?.id && hasHydratedRef.current) {
      saveFilters(activeCompany.id, {
        showFavoritesOnly,
        selectedCategories,
        selectedUserId,
        currentView,
        sortBy,
        selectedLocation: null,
        locationFilter,
        selectedSector,
        hasEmail,
        contactStatus,
        showDuplicatesOnly,
      });
    }
    hasHydratedRef.current = true;
  }, [showFavoritesOnly, selectedCategories, selectedUserId, currentView, sortBy, locationFilter, selectedSector, hasEmail, contactStatus, showDuplicatesOnly, activeCompany?.id]);

  // Fetch leads with server-side filtering + pagination
  const fetchLeads = useCallback(async () => {
    if (!activeCompany?.id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setFetchError(null);
    try {
      const result = await getLeadsPage({
        companyId: activeCompany.id,
        page,
        pageSize,
        selectedUserId,
        currentUserId: user?.id ?? null,
        showFavoritesOnly,
        selectedCategories,
        selectedSector,
        searchQuery: debouncedSearchQuery,
        sortBy,
        locationFilter: effectiveLocationFilter,
        hasEmail,
        contactStatus,
        showDuplicatesOnly,
      });
      setLeads(result.leads);
      setTotalCount(result.totalCount);
    } catch {
      setFetchError('Failed to load leads.');
    } finally {
      setIsLoading(false);
    }
  }, [activeCompany?.id, page, pageSize, selectedUserId, user?.id, showFavoritesOnly, selectedCategories, selectedSector, debouncedSearchQuery, sortBy, effectiveLocationFilter, hasEmail, contactStatus, showDuplicatesOnly]);

  // Fetch when filters/page change
  useEffect(() => {
    if (!authLoading && activeCompany?.id) {
      fetchLeads();
    }
  }, [fetchLeads, authLoading, activeCompany?.id]);

  // Reset page when filters change
  const resetPage = useCallback(() => {
    setPage(1);
    setSelectedLeadIds(new Set());
    setIsSelectAllFiltered(false);
  }, []);

  // Payment success handling (separate effect)
  useEffect(() => {
    if (authLoading) return;
    const paymentSuccess = searchParams.get('payment_success');
    if (paymentSuccess !== 'true') return;

    const orderId = searchParams.get('order_id');
    const sid = searchParams.get('sid');
    setShowSuccessMessage(true);

    if (orderId && sid) {
      supabase.functions.invoke('verify-revolut-order', {
        body: { order_id: orderId, subscription_id: sid }
      }).then(() => {
        toast({ title: "Payment Successful!", description: "Your subscription is now active." });
      }).catch(() => {
        toast({ title: "Payment Received", description: "Your subscription is being processed." });
      });
    }

    const timer = setTimeout(() => {
      setSearchParams({});
      setShowSuccessMessage(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch categories (all company categories, not just current page)
  useEffect(() => {
    if (!activeCompany?.id) return;
    supabase
      .from('leads')
      .select('category')
      .eq('company_id', activeCompany.id)
      .not('category', 'is', null)
      .then(({ data }) => {
        if (data) {
          const unique = Array.from(new Set(
            data.map(d => d.category).filter(Boolean).map(c => toTitleCase(c as string))
          )).sort();
          setCategories(unique);
        }
      });
  }, [activeCompany?.id]);

  // Fetch sectors (all company sectors, not just current page)
  useEffect(() => {
    if (!activeCompany?.id) return;
    supabase
      .from('leads')
      .select('sector')
      .eq('company_id', activeCompany.id)
      .not('sector', 'is', null)
      .then(({ data }) => {
        if (data) {
          const unique = Array.from(new Set(data.map((d: any) => d.sector).filter(Boolean) as string[])).sort();
          setSectors(unique);
        }
      });
  }, [activeCompany?.id]);

  const {
    handleToggleFavorite,
    handleDelete,
    handleBulkDelete,
    handleAddNote,
    handleAddDocument
  } = useLeadActions(setLeads);

  const {
    filteredLeads: aiFilteredLeads,
    isFiltering: isAIFiltering,
    isAIActive,
    mode: aiMode,
    smartMatch,
    freeSearch,
    clearAIFilter,
  } = useAILeadFilter(leads, activeCompany?.id, {
    locationFilter: effectiveLocationFilter,
    selectedUserId,
    currentUserId: user?.id ?? null,
  });

  const displayedLeads = isAIActive && aiFilteredLeads ? aiFilteredLeads : leads;

  const fetchCompanyUsers = useCallback(async () => {
    if (!activeCompany) return;
    try {
      setIsLoadingUsers(true);
      const users = await getCompanyMembers(activeCompany.id);
      setCompanyUsers(users.filter(u => u.id !== user?.id));
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load team members." });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [activeCompany, user?.id, toast]);

  useEffect(() => {
    if (user && activeCompany) {
      fetchCompanyUsers();
    }
  }, [user, activeCompany, fetchCompanyUsers]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    resetPage();
  }, [resetPage]);

  // Handle "select all filtered leads" across pages
  const handleSelectAllFiltered = useCallback(async () => {
    if (isSelectAllFiltered) {
      // Toggle off
      setSelectedLeadIds(new Set());
      setIsSelectAllFiltered(false);
      return;
    }
    if (!activeCompany?.id) return;

    try {
      // Fetch all IDs matching current filters (lightweight query)
      let query = supabase
        .from('leads')
        .select('id')
        .eq('company_id', activeCompany.id);

      if (selectedUserId === 'my-leads' && user?.id) {
        query = query.eq('created_by', user.id);
      } else if (selectedUserId && selectedUserId !== 'my-leads') {
        query = query.eq('created_by', selectedUserId);
      }
      if (showFavoritesOnly) {
        query = query.eq('is_favorite', true);
      }
      if (selectedCategories.length > 0) {
        const categoryFilter = selectedCategories.map(c => `category.ilike.${c}`).join(',');
        query = query.or(categoryFilter);
      }
      if (selectedSector) {
        query = query.eq('sector', selectedSector);
      }
      if (debouncedSearchQuery.trim()) {
        query = query.or(`name.ilike.%${debouncedSearchQuery.trim()}%,address.ilike.%${debouncedSearchQuery.trim()}%,category.ilike.%${debouncedSearchQuery.trim()}%`);
      }

      // Location filter
      const { buildLocationFilterString } = await import('@/services/leads/queries/getLeadsPage');
      const locFilter = buildLocationFilterString(effectiveLocationFilter);
      if (locFilter) {
        query = query.or(locFilter);
      }

      const allIds = new Set<string>();
      const BATCH = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await query.range(offset, offset + BATCH - 1);
        if (error) throw error;
        (data || []).forEach((d: any) => allIds.add(d.id as string));
        hasMore = (data?.length || 0) === BATCH;
        offset += BATCH;
      }

      setSelectedLeadIds(allIds);
      setIsSelectAllFiltered(true);
      toast({ title: 'All selected', description: `${allIds.size} leads selected across all pages.` });
    } catch (err) {
      console.error('Error selecting all filtered leads:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Could not select all leads.' });
    }
  }, [isSelectAllFiltered, activeCompany?.id, selectedUserId, user?.id, showFavoritesOnly, selectedCategories, debouncedSearchQuery, effectiveLocationFilter, toast]);

  if (authLoading || !user || !activeCompany) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            {authLoading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Initializing...</p>
              </>
            ) : (
              <>
                <p className="text-lg font-medium">No Company Selected</p>
                <p className="mt-2 text-sm text-muted-foreground">Please select a company to view leads</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-1 container mx-auto p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 w-full overflow-x-hidden">
      {showSuccessMessage && subscription && subscription.tier !== 'free' && (
        <div className="mb-8 p-5 bg-green-500/10 border border-green-500/20 rounded-xl animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-green-900 dark:text-green-100 tracking-tight">
                Subscription Activated Successfully! 🎉
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Your <span className="capitalize font-semibold">{subscription.tier}</span> subscription is now active.
              </p>
            </div>
            <button onClick={() => setShowSuccessMessage(false)} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 text-xl font-semibold flex-shrink-0">×</button>
          </div>
        </div>
      )}

      <div id="mobile-search-section">
        <LeadsSearchBar
          searchQuery={searchQuery}
          setSearchQuery={(q) => { setSearchQuery(q); resetPage(); }}
          clearSearch={clearSearch}
          onAISearch={freeSearch}
          isAISearching={isAIFiltering && aiMode === 'free_search'}
          isAIActive={isAIActive && aiMode === 'free_search'}
          onClearAI={clearAIFilter}
        />
      </div>

      <div className="w-full overflow-x-hidden">
        <LeadsHeader
          currentView={currentView}
          totalLeads={totalCount}
          showFavoritesOnly={showFavoritesOnly}
          toggleFavoritesFilter={() => { setShowFavoritesOnly(prev => !prev); resetPage(); }}
          categories={categories}
          selectedCategories={selectedCategories}
          setSelectedCategories={(cats) => { setSelectedCategories(cats); resetPage(); }}
          sectors={sectors}
          selectedSector={selectedSector}
          setSelectedSector={(s) => { setSelectedSector(s); resetPage(); }}
          isLoading={isLoading}
          companyUsers={companyUsers}
          selectedUserId={selectedUserId}
          onUserSelect={(uid) => { setSelectedUserId(uid); resetPage(); }}
          isLoadingUsers={isLoadingUsers}
          filteredLeads={displayedLeads}
          onFetchAllFilteredLeads={async () => {
            if (!activeCompany?.id) return [];
            const allLeads: Lead[] = [];
            let p = 1;
            const batchSize = 100;
            let hasMore = true;
            while (hasMore) {
              const result = await getLeadsPage({
                companyId: activeCompany.id,
                page: p,
                pageSize: batchSize,
                selectedUserId,
                currentUserId: user?.id ?? null,
                showFavoritesOnly,
                selectedCategories,
                selectedSector,
                searchQuery: debouncedSearchQuery,
                sortBy,
                locationFilter: effectiveLocationFilter,
                hasEmail,
                contactStatus,
                showDuplicatesOnly,
              });
              allLeads.push(...result.leads);
              hasMore = result.leads.length === batchSize;
              p++;
            }
            return allLeads;
          }}
          onImportComplete={fetchLeads}
          selectedLeadIds={selectedLeadIds}
          onDeleteSelected={async () => {
            await handleBulkDelete(selectedLeadIds);
            setSelectedLeadIds(new Set());
            setIsSelectAllFiltered(false);
            fetchLeads();
          }}
          sortBy={sortBy}
          onSortChange={(s) => { setSortBy(s); resetPage(); }}
          hasEmail={hasEmail}
          onHasEmailChange={(v) => { setHasEmail(v); resetPage(); }}
          locationFilter={locationFilter}
          onLocationFilterChange={(filter) => { setLocationFilter(filter); resetPage(); }}
          onSmartMatch={smartMatch}
          isAIFiltering={isAIFiltering}
          isAIActive={isAIActive}
          aiMode={aiMode}
          onClearAI={clearAIFilter}
          totalFilteredCount={totalCount}
          onSelectAllFiltered={handleSelectAllFiltered}
          isSelectAllFiltered={isSelectAllFiltered}
          contactStatus={contactStatus}
          onContactStatusChange={(s) => { setContactStatus(s); resetPage(); }}
          showDuplicatesOnly={showDuplicatesOnly}
          onShowDuplicatesChange={(v) => { setShowDuplicatesOnly(v); resetPage(); }}
        />
        {/* View Switcher Toolbar */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center justify-between px-4 py-2 mb-4 rounded-xl glass-card">
            <div className="inline-flex items-center bg-white/5 rounded-lg p-0.5 sm:p-1 gap-0.5 sm:gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={currentView === 'card' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setCurrentView('card'); setSelectedLeadIds(new Set()); setIsSelectAllFiltered(false); }}
                    className={cn(
                      "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                      currentView === 'card' && "bg-white/10"
                    )}
                  >
                    <Grid className="h-4 w-4 mr-2" />
                    <span className="hidden md:inline text-xs font-medium">Cards</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Switch to card view</p></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={currentView === 'list' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setCurrentView('list'); setSelectedLeadIds(new Set()); setIsSelectAllFiltered(false); }}
                    className={cn(
                      "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                      currentView === 'list' && "bg-white/10"
                    )}
                  >
                    <Rows className="h-4 w-4 mr-2" />
                    <span className="hidden md:inline text-xs font-medium">List</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Switch to list view</p></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={currentView === 'table' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setCurrentView('table'); setSelectedLeadIds(new Set()); setIsSelectAllFiltered(false); }}
                    className={cn(
                      "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                      currentView === 'table' && "bg-white/10"
                    )}
                  >
                    <Table2 className="h-4 w-4 mr-2" />
                    <span className="hidden md:inline text-xs font-medium">Table</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Switch to table view</p></TooltipContent>
              </Tooltip>
            </div>
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{totalCount}</span> {totalCount === 1 ? 'lead' : 'leads'}
            </p>
          </div>
        </TooltipProvider>

        <LeadsContent
          isLoading={isLoading}
          error={fetchError}
          filteredLeads={displayedLeads}
          currentView={currentView}
          searchQuery={searchQuery}
          clearSearch={clearSearch}
          isSearching={false}
          handleSearch={fetchLeads}
          handleToggleFavorite={handleToggleFavorite}
          handleDelete={async (id) => { await handleDelete(id); fetchLeads(); }}
          handleAddNote={handleAddNote}
          handleAddDocument={handleAddDocument}
          selectedLeadIds={selectedLeadIds}
          onSelectionChange={(ids) => {
            setSelectedLeadIds(ids);
            setIsSelectAllFiltered(false);
          }}
          sortBy={sortBy}
          onSortChange={(s) => { setSortBy(s); resetPage(); }}
        />
        <LeadsPagination
          page={page}
          pageSize={pageSize}
          totalCount={isAIActive ? displayedLeads.length : totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        />
      </div>

      <EnrichmentProgressBar companyId={activeCompany.id} userId={user?.id} />
    </main>
  );
};

export default Index;
